import { ButtonVariant, MeasurementType } from '../../helper/enum'
import { FormButton } from '../UI'
import {
    SitesWithAlertLevel,
    SubscriptionDetails,
} from '../../services/interfaces/map'
import Default from './AlertTypeDefault'
import useMarker from './useMarker'
import WaterLevel from './AlertTypeWaterLevel'
import useUserSubscription from '../../hooks/useUserSubscription'
import { useState } from 'react'

interface IProps {
    data: SitesWithAlertLevel
    onClose: () => void
    onSave: (userSubscriptions: SubscriptionDetails[]) => void
    setSnapshot: (userSubscriptions: SubscriptionDetails[]) => void
}

const MarkerInfo: React.FC<IProps> = ({
    data,
    onClose,
    onSave,
    setSnapshot,
}) => {
    const { alertLevels, measurementType, isAutomated } = data
    const userSubscriptionContext = useUserSubscription()
    const [showSubscriptionWarning, setShowSubscriptionWarning] = useState<boolean>(false);

    const subsDetails: SubscriptionDetails[] = alertLevels.map((alert) => ({
        alertlevelId: alert.id,
        subscriptionTypes: [],
    }))

    /*
     * Check if userSubscription is available
     * update local state with users subscription
     */
    userSubscriptionContext.userSubscription?.subscriptions?.forEach((subs) => {
        const index = subsDetails.findIndex(
            (x) => x.alertlevelId === subs.alertlevelId
        )
        subsDetails[index] = {
            ...subsDetails[index],
            subscriptionTypes: subs.subscriptionTypes,
        }
    })

    const {
        activeLevel,
        subscriptions,
        isActiveSubscription,
        hasSubscription,
        setActiveLevel,
        updateState,
    } = useMarker(subsDetails)

    const props = {
        activeLevel,
        hasSubscription,
        isActiveSubscription,
        setActiveLevel,
        updateState,
    }

    const saveSubscription = () => {
        if (userSubscriptionContext.userSubscription) {
            let userSubscriptions = [
                ...userSubscriptionContext.userSubscription.subscriptions.filter(
                    (userSubs) => {
                        return (
                            userSubs.subscriptionTypes &&
                            userSubs.subscriptionTypes.length > 0
                        )
                    }
                ),
                ...subscriptions.filter((subs) => {
                    return subs.subscriptionTypes && subs.subscriptionTypes.length > 0
                }),
            ]

            const setObj = new Set()

            userSubscriptions = userSubscriptions.reduce<SubscriptionDetails[]>(
                (acc, item) => {
                    if (!setObj.has(item.alertlevelId)) {
                        setObj.add(item.alertlevelId)
                        acc.push(item)
                    }
                    return acc
                },
                []
            )

            userSubscriptionContext.setUserSubscription({
                userId: userSubscriptionContext.userSubscription.userId,
                subscriptions: [...userSubscriptions],
            })

            onSave(subscriptions)
            setSnapshot(JSON.parse(JSON.stringify(subscriptions)))
            onClose()
        }
    }

    const onSubscriptionSave = () => {
        const existingVoiceSubscriptions = new Set(
            userSubscriptionContext.userSubscription?.subscriptions
                .filter(s => s.subscriptionTypes?.includes("Voice"))
                .map(s => s.alertlevelId)
        );

        const newVoiceSubscriptions = subscriptions.filter(s =>
            s.subscriptionTypes?.includes("Voice") && !existingVoiceSubscriptions.has(s.alertlevelId)
        );

        if (newVoiceSubscriptions.length > 0) {
            setShowSubscriptionWarning(true);
        }
        else {
            saveSubscription();
        }
    }

    const getMarkerInfoTemplate = (): JSX.Element => {
        if (!isAutomated) {
            if (measurementType === MeasurementType.WaterLevel)
                return <WaterLevel data={data} {...props} />
            else return <Default data={data} {...props} />
        } else {
            switch (measurementType) {
                case MeasurementType.WaterLevel:
                case MeasurementType.Flow:
                    return <WaterLevel data={data} {...props} />
                default:
                    return <Default data={data} {...props} />
            }
        }
    }

    if (showSubscriptionWarning) {
        return (
            <>
                <div className="flex flex-col items-center text-white text-lg">
                    <span>Calls can arrive at any hour of the day or night.</span>
                    <span>Are you sure you want to continue?</span>
                </div>
                <div className="text-center mt-8 mb-8">
                    <FormButton
                        onClick={saveSubscription}
                        variant={ButtonVariant.Secondary}
                        className="hover:opacity-80 mr-2"
                    >
                        <div className="text-wa text-black">Save</div>
                    </FormButton>
                    <FormButton
                        onClick={() => setShowSubscriptionWarning(false)}
                        variant={ButtonVariant.Secondary}
                        className="!bg-black bg-opacity-50 hover:opacity-80"
                    >
                        <div className="text-wa text-white">Back</div>
                    </FormButton>
                </div>
            </>
        )
    }

    return (
        <>
            {getMarkerInfoTemplate()}
            <div className="text-center">
                <FormButton
                    onClick={onSubscriptionSave}
                    variant={ButtonVariant.Secondary}
                    className="mt-8 mb-8 hover:opacity-80"
                >
                    <div className="text-wa text-black">Save</div>
                </FormButton>
            </div>
        </>
    )
}


export default MarkerInfo
